import { Grid, makeStyles, Typography, useTheme } from 'beam'
import classNames from 'classnames'
import flatten from 'flat'
import { Form, useFormikContext } from 'formik'
import { debounce } from 'lodash'
import { FC, ReactNodeArray, useCallback, useEffect, useMemo, useState } from 'react'
import { NavigationConfirmation } from '../../../Common/ConeRouter'
import { FormContext, FormContextType, Section } from './FormContext'
import Body from './Body'
import Description from './Description'
import Header from './Header'
import Sidebar from './Sidebar'
import UnsavedChangesDialog from './UnsavedChangesDialog'

const useStyles = makeStyles(theme => ({
  form: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    backgroundColor: '#ffffff',
  },
  formColumn: {
    overflow: 'auto',
    height: '100%',
  },
  formColumnContainer: {
    marginBottom: theme.spacing(2),
    overflow: 'hidden',
    height: '100%',
  },
  formBody: {
    flexGrow: 1,
  },
  formSidebar: {
    flexShrink: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
    maxWidth: theme.spacing(32),
  },
}))

export interface ApplicationManagementFormProps {
  /** Form title */
  title: string,
  /** Form description */
  description?: string,
  /** If true, the description will stick to the top of the form like the title, otherwise it will be under the title section */
  isStickyDescription?: boolean,
  /** An array of nodes to be put in the upper right corner, these are the action buttons of the form the formik functions like handleSubmit() will be available to these buttons */
  buttons?: ReactNodeArray,
  /** Whether or not the form will have the sidebar showing the sections */
  hasSidebar?: boolean,
  /** Determines whether or not the Form Body will be padded */
  bodyPadding?: boolean,
  /** This will cause a popup to appear when you are trying to navigate away without having saved */
  preventNavigationWhenDirty?: boolean,
  /** Overrides Formiks 'dirty' prop */
  formIsDirty?: boolean,
  onSubmit?: $TSFIXME,
}

const ApplicationManagementForm: FC<ApplicationManagementFormProps> = ({
  bodyPadding = true,
  buttons = [],
  children,
  description,
  formIsDirty = false,
  hasSidebar = true,
  isStickyDescription = false,
  preventNavigationWhenDirty = true,
  title,
}) => {
  const { dirty, errors } = useFormikContext()
  const classes = useStyles()
  const theme = useTheme()

  // Holds the data for form sections registered to this form
  const [formSections, setFormSections] = useState<Section[]>([])
  // Holds the data for form fields registered to this form
  const [formFields, setFormFields] = useState<$TSFIXME[]>([])

  // When the form fields change or the errors change, we periodically re-check for errors on the sections
  useEffect(() => {
    const debounced = debounce(() => {
      if (hasSidebar) {
        // Must flatten the errors object because the formik field names refer to the flattened version of the error path
        // Ex. errors = {flat: {field: 'error'}} = 'flat.field'
        const flattenedErrors: $TSFIXME = flatten(errors)

        // This adds all fields to the set of section errors, it is a set because we only care about whether there is an error on each section, not what the error is
        const erroredSections = Object.keys(flattenedErrors).reduce((erroredSections, fieldName) => {
          const erroredField = formFields.find(({ name }) => name === fieldName)
          if (erroredField) {
            erroredSections.add(erroredField.sectionId)
          }
          return erroredSections
        }, new Set())

        // Sets all sections to either valid or invalid based on the errors detected from the section fields
        setFormSections(formSections =>
          formSections.map(section => ({
            ...section,
            isValid: !erroredSections.has(section.sectionId),
          })),
        )
      }
    }, 500)
    return debounced.cancel
  }, [errors, formFields, hasSidebar, setFormSections],
  )

  // This creates the context to be passed down
  const formContext = useMemo<FormContextType>(() => ({
    sections: formSections,
    fields: formFields,
    addFormSection(section) {
      setFormSections(sections => {
        // For first section set active
        if (!sections.length) {
          section.isActive = true
        }
        const sectionExists = sections.filter(existingSection => existingSection.title === section.title)
        if (sectionExists.length > 0) {
          return sections
        } else {
          return [...sections, section]
        }
      })
    },
    removeFormSection(sectionId) {
      setFormSections(sections => sections.filter(sec => sec.sectionId !== sectionId))
    },
    setActiveSection({ sectionId }) {
      setFormSections(sections => sections.map(sec => ({ ...sec, isActive: sec.sectionId === sectionId })))
    },
    addFormField(sectionId, field) {
      setFormFields(formFields => [...formFields, { ...field, sectionId }])
    },
    removeFormField(sectionId, { fieldId }) {
      setFormFields(formFields => formFields.filter(field => field.fieldId !== fieldId || field.sectionId !== sectionId))
    },
  }), [formFields, formSections, setFormFields, setFormSections])

  const scrollHandler = useCallback(({ target }) => {
    if (!hasSidebar) return
    const parentTop = target.scrollTop
    formContext.sections.some(section => {
      const { ref } = section
      if (ref.current && ref.current.offsetTop > parentTop) {
        if (!section.isActive) {
          formContext.setActiveSection(section)
        }
        return true
      } else {
        return false
      }
    })
  }, [formContext, hasSidebar])

  return (
    <FormContext.Provider value={formContext}>
      {/* if the form is dirty we show the navigation confirmation dialog this will go away automatically when dismissed */}
      {preventNavigationWhenDirty && (dirty || formIsDirty) && (
        <NavigationConfirmation functionAsChild={false}>
          {confirm => <UnsavedChangesDialog onYes={() => confirm(true)} onNo={() => confirm(false)} open={true} />}
        </NavigationConfirmation>
      )}

      <Form className={classes.form}>
        <Header
          title={title}
          description={description && isStickyDescription ? <Typography variant="body1">{description}</Typography> : null}
          buttons={buttons}
        />
        <Grid
          container
          component="div"
          wrap="nowrap"
          direction="row-reverse"
          onScroll={scrollHandler}
          className={classes.formColumnContainer}
          style={{ marginBottom: bodyPadding ? theme.spacing(2) : 0 }}
        >
          <Grid item className={classNames(classes.formColumn, classes.formBody)}>
            <Body bodyPadding={bodyPadding}>
              {description && !isStickyDescription && <Description description={description} />}
              {children}
            </Body>
          </Grid>
          {hasSidebar && (
            <Grid item className={classNames(classes.formColumn, classes.formSidebar)}>
              <Sidebar />
            </Grid>
          )}
        </Grid>
      </Form>
    </FormContext.Provider>
  )
}

export default ApplicationManagementForm
